"use client";

import { useSetQuantity } from "@frend-digital/centra/client";
import type {
  BaseSelection,
  BaseSelectionItem,
} from "@frend-digital/centra-types/selection";
import { gtag } from "@frend-digital/events/gtm";
import Minus from "@/components/icons/Minus";
import Plus from "@/components/icons/Plus";
import { useBeforeGtmEvent } from "@/features/global/Events";
import { klaviyoTracker } from "@/lib/klaviyo";
import { selectionItemToGTAGProduct } from "@/lib/utils/gtm";
import styles from "./quantity-selector.module.css";

interface IQuantitySelectorProps {
  item: BaseSelectionItem;
}

const QuantitySelector = ({ item }: IQuantitySelectorProps) => {
  const { quantity, line } = item;
  const setQuantity = useSetQuantity({
    optimistic: true,
  });
  const beforeGtmEvent = useBeforeGtmEvent();

  const onAdd = () => {
    const newQuantity = (quantity ?? 0) + 1;

    setQuantity.mutate(
      {
        line: line ?? "",
        quantity: newQuantity,
      },
      {
        onSuccess: data => {
          if (
            data.selection?.currency &&
            data.selection?.totals.grandTotalPriceAsNumber
          ) {
            gtag.addToCart(
              {
                currency: data.selection.currency,
                value: data.selection.totals.grandTotalPriceAsNumber,
                items: (data.selection.items ?? []).map(item =>
                  selectionItemToGTAGProduct(item, data.selection!.currency!),
                ),
              },
              beforeGtmEvent,
            );
          }
          klaviyoTracker.addToCart(data.selection as BaseSelection, item);
        },
      },
    );
  };

  const onSubtract = () => {
    const newQuantity = (quantity ?? 1) - 1;

    setQuantity.mutate(
      {
        line: line ?? "",
        quantity: newQuantity,
      },
      {
        onSuccess: data => {
          if (
            data.selection?.currency &&
            data.selection?.totals.grandTotalPriceAsNumber
          ) {
            gtag.removeFromCart(
              {
                currency: data.selection.currency,
                value: data.selection.totals.grandTotalPriceAsNumber,
                items: (data.selection.items ?? []).map(item =>
                  selectionItemToGTAGProduct(item, data.selection!.currency!),
                ),
              },
              beforeGtmEvent,
            );
          }
          klaviyoTracker.removeFromCart(
            data?.selection as BaseSelection,
            false,
            item,
          );
        },
      },
    );
  };

  return (
    <span className={styles["quantity-container"]}>
      <button type="button" title="Decrease" onClick={onSubtract}>
        <Minus />
      </button>
      <p>{quantity}</p>
      <button type="button" title="Increase" onClick={onAdd}>
        <Plus />
      </button>
    </span>
  );
};

export default QuantitySelector;
